import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useNotification } from "config/Notification";

import LoadingOverlay from "components/LoadingOverlay.jsx";

import { fetchAssessmentReportdetailsforReview as fetchAssessmentReportdetailsforReviewService } from "services/modules/cap/standardservice";
import { putAssessmentStatus as putAssessmentStatusService } from "services/modules/cap/assessmentservice";
import { patchAssessmentDetailScore as patchAssessmentDetailScoreService } from "services/modules/cap/standardservice";

import "../../styles/cap.css";

import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import CreditScore from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import {
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import QuestionRenderer from "../../components/shared/QuestionRenderer";

// ─── Score options — label shown to reviewer, numeric value sent to backend only
const SCORE_OPTIONS = [
  { value: 4, label: "Fully Satisfied",    color: "#227C10" },
  { value: 3, label: "Largely Satisfied",  color: "#50E232" },
  { value: 2, label: "Partially Satisfied",color: "#BF9D12" },
  { value: 1, label: "Minimally Satisfied",color: "#B60000" },
  { value: 0, label: "Not Satisfied",      color: "#333333" },
];

// Returns the SCORE_OPTIONS entry for a given numeric score value
const getScoreOption = (score) =>
  SCORE_OPTIONS.find((o) => o.value === score) ?? null;

const ReviewAssessmentPage = () => {
  const location = useLocation();
  const urlparams = location.state || {};
  const navigate = useNavigate();
  const { openNotification } = useNotification();

  const [assessmentsList, setAssessmentsList] = useState([]);
  const [confirmReject, setConfirmReject] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [isApprove, setIsApprove] = useState(false);
  const [isReject, setIsReject] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [overallSummary, setOverallSummary] = useState("");

  const [assessmentStatus, setAssessmentStatus] = useState("");
  const [rejectionSummary, setRejectionSummary] = useState("");
  const [rejectionHistory, setRejectionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const [approvalSummary, setApprovalSummary] = useState("");
  const [approvedBy, setApprovedBy] = useState(null);
  const [approvedAt, setApprovedAt] = useState(null);

  const [manualScores, setManualScores] = useState({});

  // ─── Details dialog state ─────────────────────────────────────────────────
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedSubcontrol, setSelectedSubcontrol] = useState(null);

  const openDetailDialog = (subcontrol) => {
    setSelectedSubcontrol(subcontrol);
    setDetailDialog(true);
  };

  const closeDetailDialog = () => {
    setDetailDialog(false);
    setSelectedSubcontrol(null);
  };

  // ─── Score handler ────────────────────────────────────────────────────────
  // KEY FIX: use subcontrolID (always present) as the map key, NOT _id which
  // can be undefined for non-text_box rows, causing all scores to collide.
  const handleManualScore = async (subcontrolID, backendId, score) => {
    const parsed = parseFloat(score);
    if (isNaN(parsed) || parsed < 0 || parsed > 4) return;
    // Store by subcontrolID so each question row has its own isolated entry
    setManualScores((prev) => ({ ...prev, [subcontrolID]: parsed }));
    try {
      // Send the numeric value to backend for calculations (backendId = _id)
      if (backendId) {
        await patchAssessmentDetailScoreService(backendId, parsed);
        openNotification("Response updated", "success");
      }
      else {
      openNotification("Response Not saved", "error");        
      }
    } catch (err) {
      openNotification("Response Not saved", "error");
    }
  };

  let body = { assessment_id: urlparams.assessmentid };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchPendingAssessments();
  }, []);

  const reviewAssessment = async () => {
    try {
      body.status = "Review Assessment In Progress";
      const response = await putAssessmentStatusService(body);
      if (response.status === 200 || response.status === 201) {
        urlparams.status = "Review Assessment In Progress";
        await fetchPendingAssessments();
      } else {
        openNotification("Failed to update assessment status", "error");
      }
    } catch (error) {
      openNotification("Error while updating assessment status", "error");
    }
  };

  const fetchPendingAssessments = async () => {
    setLoading(true);
    try {
      let params = { assessmentid: urlparams.assessmentid };
      const response = await fetchAssessmentReportdetailsforReviewService(params);

      if (Array.isArray(response.data)) {
        setAssessmentsList(response.data);
      } else {
        setAssessmentsList(response.data.controls || []);
        setAssessmentStatus(response.data.status || "");
        setRejectionSummary(response.data.overall_rejection_remarks || "");
        setRejectionHistory(response.data.assessment_rejection_history || []);
        setApprovalSummary(response.data.approval_remarks || "");
        setApprovedBy(response.data.approved_by || null);
        setApprovedAt(response.data.approved_at || null);
      }
    } catch (error) {
      openNotification("Error fetching pending assessments", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      body.status = "Approved";
      body.approvalRemarks = overallSummary;
      let response = await putAssessmentStatusService(body);
      if (response.status === 200 || response.status === 201) {
        navigate("/cap/assignmitigation", {
          state: {
            assessmentid: urlparams.assessmentid,
            standard: urlparams.standard,
            status: "Approved",
          },
        });
      } else {
        openNotification("Failed to update assessment status", "error");
      }
    } catch (error) {
      openNotification("Error while approving assessment", "error");
    }
  };

  const handleReject = async () => {
    try {
      navigate("/cap/rejectassessment", {
        state: {
          assessmentid: urlparams.assessmentid,
          standard: urlparams.standard,
          status: assessmentStatus,
        },
      });
    } catch (error) {
      openNotification("Error while rejecting assessment", "error");
    }
  };

  // ─── Score chip color helper (unused — replaced by getScoreOption color) ──

  return (
    <div>
      {/* ── Page header ── */}
      <div className="row py-1">
        <p>
          <img className="px-1 icons-style" src="../assets/icons/003-clipboard.svg" alt="View" />
          <b className="px-1">Review assessment </b>
          <img className="px-1 icons-style" src="../assets/icons/right-layer.svg" alt="View" />
          {urlparams.standard === "CIS" && <img className="px-sm standard-icon-size" src="../assets/icons/standard-cis.png" draggable="false" alt="CIS-Icon" />}
          {urlparams.standard === "ISO-27001" && <img className="px-sm standard-icon-size" src="../assets/icons/standard-iso-27001.png" draggable="false" alt="ISO-Icon" />}
          {urlparams.standard === "NIST" && <img className="px-sm standard-icon-size" src="../assets/icons/standard-nist.png" draggable="false" alt="NIST-Icon" />}
          {urlparams.standard === "COBIT" && <img className="px-sm standard-icon-size" src="../assets/icons/standard-cobit.png" draggable="false" alt="COBIT-Icon" />}
          {urlparams.standard === "TISAX" && <img className="px-sm standard-icon-size" src="../assets/icons/standard-tisax.png" draggable="false" alt="TISAX-Icon" />}
          {urlparams.standard === "NIS 2.0" && <img className="px-sm standard-icon-size" src="../assets/icons/standard-nis2.png" draggable="false" alt="NIS-Icon" />}
          {urlparams.standard === "Ransomware" && <img className="px-sm standard-icon-size" src="../assets/icons/standard-ransomware.png" draggable="false" alt="Ransomware-Icon" />}
          {urlparams.standard === "Data Protection" && <img className="px-sm standard-icon-size" src="../assets/icons/standard-data-protection.png" draggable="false" alt="DP-Icon" />}
          {urlparams.standard === "Supplier Risk" && <img className="px-sm standard-icon-size" src="../assets/icons/standard-supplier-risk.png" draggable="false" alt="SR-Icon" />}
          <span> {urlparams.standard} </span> – <span>{urlparams.assessmentid}</span>
        </p>
      </div>

      {loading && <LoadingOverlay />}

      <div>
        {assessmentsList.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={3}>Control name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? assessmentsList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : assessmentsList
                ).map((item) => (
                  <React.Fragment key={item.controlID}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          className="expand-row"
                          size="small"
                          onClick={() =>
                            setExpandedRow((prev) =>
                              prev === item.controlID ? null : item.controlID
                            )
                          }
                        >
                          {expandedRow === item.controlID
                            ? <KeyboardArrowUpIcon />
                            : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{item.controlID + " : " + item.controlName}</TableCell>
                      <TableCell>{item.assessment_id}</TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={3}>
                        <Collapse in={expandedRow === item.controlID} timeout="auto" unmountOnExit>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: "8%" }}>Ref</TableCell>
                                <TableCell sx={{ width: "35%" }}>Control Question</TableCell>
                                <TableCell sx={{ width: "25%" }}>Question Response</TableCell>
                                <TableCell sx={{ width: "15%" }}>Control Status</TableCell>
                                {isReject && <TableCell>Reject remarks</TableCell>}
                                {/* Rationale removed — now in dialog */}
                                <TableCell align="center" sx={{ width: "12%" }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {item.subcontrols.map((subcontrol) => {
                                const qType = subcontrol.question_type || "five_option";
                                const isTextBox = qType === "text_box";
                                const canReview =
                                  assessmentStatus === "Submitted" ||
                                  assessmentStatus === "Review Assessment In Progress";

                                return (
                                  <TableRow key={subcontrol.subcontrolID}>
                                    {/* Ref */}
                                    <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "middle" }}>
                                      {subcontrol.subcontrol}
                                    </TableCell>

                                    {/* Control name */}
                                    <TableCell sx={{ verticalAlign: "middle" }}>
                                      <div className="subcontrols">{subcontrol.subcontrolName}</div>
                                    </TableCell>

                                    {/* Answer — clean read-only display */}
                                    <TableCell sx={{ verticalAlign: "middle" }}>
                                      <QuestionRenderer
                                        question_type={qType}
                                        answer_config={subcontrol.answer_config || null}
                                        value={subcontrol.answer_value || subcontrol.severity}
                                        readOnly={true}
                                      />
                                    </TableCell>

                                    {/* Score — dropdown for text_box reviewer, satisfaction chip otherwise */}
                                    <TableCell sx={{ verticalAlign: "middle" }}>
                                      {isTextBox && canReview ? (
                                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                          <Select
                                            size="small"
                                            displayEmpty
                                            value={manualScores[subcontrol.subcontrolID] ?? ""}
                                            onChange={(e) =>
                                              handleManualScore(
                                                subcontrol.subcontrolID,
                                                subcontrol.submissionID,
                                                e.target.value,
                                              )
                                            }
                                            sx={{ minWidth: 180, fontSize: "13px" }}
                                            renderValue={(val) => {
                                              if (val === "" || val === null || val === undefined) {
                                                return <span style={{ color: "#aaa" }}>Rate satisfaction…</span>;
                                              }
                                              const opt = getScoreOption(val);
                                              return opt ? (
                                                <span style={{ color: opt.color, fontWeight: 600 }}>
                                                  {opt.label}
                                                </span>
                                              ) : val;
                                            }}
                                          >
                                            {SCORE_OPTIONS.map((opt) => (
                                              <MenuItem key={opt.value} value={opt.value}>
                                                <span style={{ color: opt.color, fontWeight: 600, fontSize: "13px" }}>
                                                  {opt.label}
                                                </span>
                                              </MenuItem>
                                            ))}
                                          </Select>

                                          {/* Tooltip moved to a small info icon — no longer wraps the Select */}
                                          <Tooltip title="How satisfied are you with this answer?" placement="top">
                                            <InfoOutlinedIcon
                                              sx={{ fontSize: 16, color: "#aaa", cursor: "help", flexShrink: 0 }}
                                            />
                                          </Tooltip>
                                        </div>
                                      ) : (
                                        (() => {
                                          const score = manualScores[subcontrol.subcontrolID] ?? subcontrol.score_contribution;
                                          const opt = getScoreOption(score);
                                          return opt ? (
                                            <Chip
                                              label={opt.label}
                                              size="small"
                                              sx={{
                                                backgroundColor: opt.color,
                                                color: "#fff",
                                                fontSize: "11px",
                                                fontWeight: 600,
                                              }}
                                            />
                                          ) : (
                                            <span style={{ color: "#aaa", fontSize: "13px" }}>—</span>
                                          );
                                        })()
                                      )}
                                    </TableCell>

                                    {/* Actions — View Details button */}
                                    <TableCell align="center" sx={{ verticalAlign: "middle" }}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<InfoOutlinedIcon />}
                                        onClick={() => openDetailDialog(subcontrol)}
                                        sx={{
                                          textTransform: "none",
                                          fontSize: "12px",
                                          whiteSpace: "nowrap",
                                          borderColor: "#1976d2",
                                          color: "#1976d2",
                                          "&:hover": {
                                            backgroundColor: "#e3f2fd",
                                          },
                                        }}
                                      >
                                        View Details
                                      </Button>
                                      {/* US-CAP-302: Quick visual cue that evidence was attached — full list + download is in the dialog */}
                                      {(subcontrol.evidence_files || []).length > 0 && (
                                        <Tooltip title={`${subcontrol.evidence_files.length} evidence file(s) attached`} placement="top">
                                          <Chip
                                            icon={<AttachFileIcon sx={{ fontSize: "14px !important" }} />}
                                            label={subcontrol.evidence_files.length}
                                            size="small"
                                            sx={{ ml: 1, height: 22, fontSize: "11px", backgroundColor: "#e8f0fe", color: "#1a2b5e" }}
                                          />
                                        </Tooltip>
                                      )}
                                    </TableCell>

                                    {/* Reject remarks — only when isReject */}
                                    {isReject && (
                                      <TableCell sx={{ verticalAlign: "middle" }}>
                                        {subcontrol.assessment_comments ?? "—"}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
              component="div"
              count={assessmentsList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}

        {/* ── Summary section ── */}
        {assessmentsList.length > 0 && (
          <div className="mt-4">
            <div className="card shadow-sm p-3" style={{ border: "1px solid #e3e6f0", width: "100%" }}>

              {/* REJECTED MODE */}
              {assessmentStatus === "Reject Assessment" && (
                <>
                  <label className="font-weight-bold text-primary d-block mb-2">
                    Overall Rejection Summary
                  </label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={rejectionSummary}
                    readOnly
                    style={{ background: "#fff", border: "none", boxShadow: "none", outline: "none", resize: "none" }}
                  />
                  {rejectionHistory.length > 1 && (
                    <div className="text-right mt-2">
                      <Button size="small" onClick={() => setShowHistory(!showHistory)}>
                        {showHistory ? "Hide History" : "View Full Rejection History"}
                      </Button>
                    </div>
                  )}
                  {showHistory && (
                    <div className="mt-3 p-3 border rounded bg-light">
                      {[...rejectionHistory]
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((item, index) => (
                          <div key={item.iteration || index} className="mb-3 pb-2 border-bottom">
                            <div style={{ fontSize: "0.85rem", color: "#6c757d" }}>
                              <strong>Iteration:</strong> {item.iteration}<br />
                              <strong>Date:</strong>{" "}
                              {item.created_at ? new Date(item.created_at).toLocaleString() : "N/A"}
                            </div>
                            <div className="mt-2 text-dark">{item.assessment_rejection_remarks}</div>
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}

              {/* APPROVED MODE */}
              {(assessmentStatus === "Approved" ||
                assessmentStatus === "Mitigation In Progress" ||
                assessmentStatus === "Mitigation Initiated") && (
                <>
                  <label className="font-weight-bold text-primary d-block mb-2">Approval Summary</label>
                  <textarea
                    className="form-control p-0"
                    rows="3"
                    value={approvalSummary}
                    readOnly
                    style={{ background: "#fff", border: "none", boxShadow: "none", outline: "none", resize: "none" }}
                  />
                  {approvedAt && (
                    <small className="text-muted mt-2 d-block">
                      Approved on: {new Date(approvedAt).toLocaleString()}
                    </small>
                  )}
                </>
              )}

              {/* REVIEW MODE */}
              {(assessmentStatus === "Submitted" ||
                assessmentStatus === "Review Assessment In Progress") && (
                <>
                  <label className="font-weight-bold text-primary">
                    Overall Assessment Approval Summary (Max 500 characters)
                    <span style={{ color: "red", fontSize: "18px", marginLeft: "4px" }}>*</span>
                  </label>
                  <textarea
                    className="form-control p-0"
                    rows="4"
                    maxLength="500"
                    placeholder="Provide approval summary..."
                    value={overallSummary}
                    onChange={(e) => setOverallSummary(e.target.value)}
                  />
                  <div className="text-right mt-1">
                    <small className={overallSummary.length >= 500 ? "text-danger" : "text-muted"}>
                      {overallSummary.length}/500 characters
                    </small>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Action buttons ── */}
        {assessmentsList.length > 0 && (
          <div className="assessments-list">
            <button className="go-back-button w-150" onClick={() => navigate("/cap/pending-assessment")}>
              <ArrowLeftIcon /> Go back
            </button>
            {(assessmentStatus === "Submitted" || assessmentStatus === "Review Assessment In Progress") && (
              <>
                <button className="approve-button mx-4 w-150" onClick={() => setIsApprove(true)}>
                  Approve
                </button>
                <button className="reject-button w-150" onClick={() => setConfirmReject(true)}>
                  Reject
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Approve dialog ── */}
        {isApprove && (
          <Dialog onClose={() => setIsApprove(false)} open={isApprove}>
            <DialogTitle sx={{ m: 0, p: 2 }} />
            <DialogContent>
              <Typography gutterBottom>
                <div className="audit-trail-popuptext">
                  <div className="popup-body">
                    <div className="txt-c">
                      <CreditScore color="success" className="text-size" />
                    </div>
                    <div className="padd-l20">
                      <div className="my-4 txt-c clr-b">
                        <b>Do you want to approve assessment?</b>
                      </div>
                      {overallSummary && (
                        <div className="mb-3 p-2 bg-light text-dark rounded border" style={{ fontSize: "0.9rem" }}>
                          <strong>Summary Preview:</strong>
                          <p className="mb-0 italic">{overallSummary}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Button variant="contained" className="btn-card danger" onClick={() => setIsApprove(false)}>
                      No, i will review later
                    </Button>
                    <Button variant="contained" className="btn-card" onClick={() => handleApprove()}>
                      Yes, approve
                    </Button>
                  </div>
                </div>
              </Typography>
            </DialogContent>
          </Dialog>
        )}

        {/* ── Confirm reject dialog ── */}
        {confirmReject && (
          <Dialog onClose={() => setConfirmReject(false)} open={confirmReject}>
            <DialogTitle sx={{ m: 0, p: 2 }} />
            <DialogContent>
              <Typography gutterBottom>
                <div className="audit-trail-popuptext">
                  <div className="popup-body">
                    <div className="txt-c">
                      <img src="../assets/icons/alert.svg" className="w-40" draggable="false" alt="" />
                    </div>
                    <div className="padd-l20">
                      <div className="my-4 txt-c clr-b">
                        <b>Are you sure you want to reject the assessment?</b>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Button variant="contained" className="btn-card danger" onClick={() => setConfirmReject(false)}>
                      Cancel
                    </Button>
                    <Button variant="contained" className="btn-card" onClick={() => handleReject()}>
                      Yes
                    </Button>
                  </div>
                </div>
              </Typography>
            </DialogContent>
          </Dialog>
        )}

        {/* ── View Details dialog ── */}
        {detailDialog && selectedSubcontrol && (
          <Dialog
            open={detailDialog}
            onClose={closeDetailDialog}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "10px" } }}
          >
            <DialogTitle sx={{ m: 0, p: 2, backgroundColor: "#1a2b5e", color: "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                <span style={{ fontSize: "15px", fontWeight: 600 }}>
                  {selectedSubcontrol.subcontrol} — Question Details
                </span>
              </div>
              <IconButton
                aria-label="close"
                onClick={closeDetailDialog}
                sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>

              {/* Control name */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
                  Control Question
                </div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#1a2b5e" }}>
                  {selectedSubcontrol.subcontrolName}
                </div>
              </div>

              <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

              {/* Answer */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                  Answer Given
                </div>
                <QuestionRenderer
                  question_type={selectedSubcontrol.question_type || "five_option"}
                  answer_config={selectedSubcontrol.answer_config || null}
                  value={selectedSubcontrol.answer_value || selectedSubcontrol.severity}
                  readOnly={true}
                />
              </div>

              <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

              {/* Score — always read live from manualScores so dialog reflects
                  updates made in the table without needing to reopen */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                  Satisfaction Score
                </div>
                {(() => {
                  // manualScores[subcontrolID] is the live value set by the reviewer.
                  // Only fall back to score_contribution if the reviewer has never
                  // touched this question in the current session.
                  const hasManualEntry = Object.prototype.hasOwnProperty.call(
                    manualScores,
                    selectedSubcontrol.subcontrolID,
                  );
                  const score = hasManualEntry
                    ? manualScores[selectedSubcontrol.subcontrolID]
                    : selectedSubcontrol.score_contribution ?? null;
                  const opt = getScoreOption(score);
                  return opt ? (
                    <Chip
                      label={opt.label}
                      size="small"
                      sx={{ backgroundColor: opt.color, color: "#fff", fontSize: "12px", fontWeight: 600 }}
                    />
                  ) : (
                    <span style={{ color: "#aaa", fontSize: "13px" }}>Score not given</span>
                  );
                })()}
              </div>

              <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

              {/* US-CAP-302: Evidence — files the user attached at the self-assessment stage.
                  Each link is a short-lived presigned S3 URL returned by the backend. */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                  Evidence
                </div>
                {(selectedSubcontrol.evidence_files || []).length > 0 ? (
                  (selectedSubcontrol.evidence_files || []).map((file, idx) => (
                    <div
                      key={file.key || idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        fontSize: 13,
                        padding: "6px 10px",
                        marginBottom: 4,
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: 6,
                      }}
                    >
                      <span style={{ overflowWrap: "break-word", flex: "1 1 auto", minWidth: 0 }}>
                        📎 {file.filename}
                      </span>
                      {file.url ? (
                        <Button
                          size="small"
                          variant="outlined"
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ flex: "0 0 auto", textTransform: "none", fontSize: "12px" }}
                        >
                          Download
                        </Button>
                      ) : (
                        <span style={{ color: "#aaa", fontSize: "12px" }}>Unavailable</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ color: "#aaa", fontSize: "13px" }}>No evidence attached</div>
                )}
              </div>

              <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

              {/* Rationale */}
              <div style={{ marginBottom: selectedSubcontrol.assessment_comments ? "16px" : 0 }}>
                <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                  Rationale for Rating
                </div>
                <div
                  style={{
                    background: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "6px",
                    padding: "10px 14px",
                    fontSize: "13px",
                    lineHeight: "1.6",
                    color: selectedSubcontrol.rational ? "#333" : "#aaa",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {selectedSubcontrol.rational || "No rationale provided"}
                </div>
              </div>

              {/* Reject remarks — only if present */}
              {selectedSubcontrol.assessment_comments && (
                <>
                  <hr style={{ margin: "12px 0", borderColor: "#eee" }} />
                  <div>
                    <div style={{ fontSize: "11px", color: "#B60000", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                      Reject Remarks
                    </div>
                    <div
                      style={{
                        background: "#fff5f5",
                        border: "1px solid #ffcccc",
                        borderRadius: "6px",
                        padding: "10px 14px",
                        fontSize: "13px",
                        lineHeight: "1.6",
                        color: "#B60000",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedSubcontrol.assessment_comments}
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default ReviewAssessmentPage;